import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  Platform,
  Modal,
  PanResponder,
  Animated,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';

const { width, height } = Dimensions.get('window');
const CANVAS_MIN_WIDTH = Math.max(width * 6, 3000);
const CANVAS_MIN_HEIGHT = Math.max(height * 6, 3000);
const CANVAS_PADDING = 150;
const OBJECT_BOX_WIDTH = 280;
const OBJECT_BOX_HEIGHT = 180;
const LEGEND_WIDTH = 148;
const LEGEND_HEIGHT = 108;

export default function SchemaBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState(new Set());
  const [tableFields, setTableFields] = useState({});
  const [relationships, setRelationships] = useState([]);
  const [junctionTables, setJunctionTables] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [objectPositions, setObjectPositions] = useState({});
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [draggingTable, setDraggingTable] = useState(null);
  
  // Keep draggingTable ref in sync
  useEffect(() => {
    draggingTableRef.current = draggingTable;
  }, [draggingTable]);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [leftPanelMinimized, setLeftPanelMinimized] = useState(false);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [canvasLayout, setCanvasLayout] = useState({ width: 0, height: 0 });
  const [legendDragging, setLegendDragging] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [canvasScale, setCanvasScale] = useState(1);
  const canvasRef = useRef(null);
  const canvasScrollRef = useRef(null);
  const pinchResponderRef = useRef(null);
  const canvasDragResponderRef = useRef(null);
  const canvasMapPanRef = useRef(null);
  const scrollerTrackWidthRef = useRef(280);
  const legendPanRef = useRef(null);
  const legendDataRef = useRef(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });
  const canvasLayoutRef = useRef({ width: 0, height: 0 });
  const canvasScaleRef = useRef(1);
  const lastPositionsRef = useRef({});
  const draggingTableRef = useRef(null);
  const objectPositionsRef = useRef({});
  const panRespondersRef = useRef({});
  
  // Keep refs in sync with state
  useEffect(() => {
    draggingTableRef.current = draggingTable;
  }, [draggingTable]);
  
  useEffect(() => {
    objectPositionsRef.current = objectPositions;
  }, [objectPositions]);

  // Lock orientation to landscape on mobile when page is focused
  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS === 'web') {
        return; // Skip orientation lock on web
      }
      
      // Dynamically import to avoid webpack bundling on web
      let orientationLockPromise = null;
      let orientationUnlockPromise = null;
      
      const lockOrientation = async () => {
        try {
          const ScreenOrientation = await import('expo-screen-orientation');
          if (ScreenOrientation && ScreenOrientation.lockAsync) {
            orientationLockPromise = ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE);
            await orientationLockPromise;
          }
        } catch (err) {
          // Silently fail - orientation lock is optional
        }
      };
      
      lockOrientation();
      
      return () => {
        // Unlock orientation when leaving the page
        const unlockOrientation = async () => {
          try {
            const ScreenOrientation = await import('expo-screen-orientation');
            if (ScreenOrientation && ScreenOrientation.unlockAsync) {
              orientationUnlockPromise = ScreenOrientation.unlockAsync();
              await orientationUnlockPromise;
            }
          } catch (err) {
            // Silently fail - orientation unlock is optional
          }
        };
        unlockOrientation();
      };
    }, [])
  );

  useEffect(() => {
    // Get user from route params
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
    } else if (route?.params?.userId) {
      // Fetch user if only userId is provided
      fetch(`${getApiUrl()}/api/users/${route.params.userId}`)
        .then(res => res.json())
        .then(data => setCurrentUser(data))
        .catch(err => console.error('Error fetching user:', err));
    }
  }, [route]);

  useEffect(() => {
    fetchSchemaData();
  }, []);

  // Pinch-to-zoom handler
  useEffect(() => {
    if (Platform.OS !== 'web') {
      let lastDistance = null;
      
      pinchResponderRef.current = PanResponder.create({
        onStartShouldSetPanResponder: () => false,
        onMoveShouldSetPanResponder: (evt, gestureState) => {
          return evt.nativeEvent.touches.length === 2;
        },
        onPanResponderGrant: () => {
          lastDistance = null;
        },
        onPanResponderMove: (evt) => {
          if (evt.nativeEvent.touches.length === 2) {
            const touch1 = evt.nativeEvent.touches[0];
            const touch2 = evt.nativeEvent.touches[1];
            const distance = Math.sqrt(
              Math.pow(touch2.pageX - touch1.pageX, 2) +
              Math.pow(touch2.pageY - touch1.pageY, 2)
            );
            
            if (lastDistance !== null) {
              const scaleChange = distance / lastDistance;
              const newZoom = Math.max(0.5, Math.min(2, zoomLevel * scaleChange));
              setZoomLevel(newZoom);
              setCanvasScale(newZoom);
            }
            lastDistance = distance;
          }
        },
        onPanResponderRelease: () => {
          lastDistance = null;
        },
      });
    }
  }, [zoomLevel]);

  const handleZoomIn = () => {
    const newZoom = Math.min(2, zoomLevel + 0.1);
    setZoomLevel(newZoom);
    setCanvasScale(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(0.5, zoomLevel - 0.1);
    setZoomLevel(newZoom);
    setCanvasScale(newZoom);
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setCanvasScale(1);
  };

  useEffect(() => {
    scrollPositionRef.current = scrollPosition;
  }, [scrollPosition]);

  useEffect(() => {
    canvasScaleRef.current = canvasScale;
  }, [canvasScale]);

  const getMaxScrollX = () => {
    const viewportWidth = canvasLayoutRef.current.width || width;
    const contentWidth = (CANVAS_MIN_WIDTH + CANVAS_PADDING * 2) * canvasScaleRef.current;
    return Math.max(0, contentWidth - viewportWidth);
  };

  const getMaxScrollY = () => {
    const viewportHeight = canvasLayoutRef.current.height || height;
    const contentHeight = (CANVAS_MIN_HEIGHT + CANVAS_PADDING * 2) * canvasScaleRef.current;
    return Math.max(0, contentHeight - viewportHeight);
  };

  const getCanvasPanSensitivity = () => {
    const maxScrollX = getMaxScrollX();
    const trackWidth = scrollerTrackWidthRef.current || 280;
    return Math.max(2.5, maxScrollX / Math.max(100, trackWidth));
  };

  const scrollCanvasTo = (x, y) => {
    const nextX = Math.min(getMaxScrollX(), Math.max(0, x));
    const nextY = Math.min(getMaxScrollY(), Math.max(0, y));
    canvasScrollRef.current?.scrollTo({ x: nextX, y: nextY, animated: false });
    setScrollPosition({ x: nextX, y: nextY });
  };

  const handleCanvasScroll = (event) => {
    const { contentOffset } = event.nativeEvent;
    setScrollPosition({ x: contentOffset.x, y: contentOffset.y });
  };

  const applyViewportFromWorldCoords = (worldLeft, worldTop, viewportW, viewportH) => {
    const maxWorldLeft = Math.max(0, CANVAS_MIN_WIDTH - viewportW);
    const maxWorldTop = Math.max(0, CANVAS_MIN_HEIGHT - viewportH);
    const clampedLeft = Math.min(Math.max(0, worldLeft), maxWorldLeft);
    const clampedTop = Math.min(Math.max(0, worldTop), maxWorldTop);

    const newScrollX = clampedLeft * canvasScaleRef.current - CANVAS_PADDING;
    const newScrollY = clampedTop * canvasScaleRef.current - CANVAS_PADDING;

    scrollCanvasTo(newScrollX, newScrollY);
  };

  useEffect(() => {
    let startWorld = { left: 0, top: 0 };

    legendPanRef.current = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => {
        const data = legendDataRef.current;
        if (!data) return;
        startWorld = { left: data.viewportLeft, top: data.viewportTop };
        setLegendDragging(true);
      },
      onPanResponderMove: (_, gestureState) => {
        const data = legendDataRef.current;
        if (!data) return;

        const newWorldLeft = startWorld.left + gestureState.dx / data.scale;
        const newWorldTop = startWorld.top + gestureState.dy / data.scale;
        applyViewportFromWorldCoords(
          newWorldLeft,
          newWorldTop,
          data.viewportW,
          data.viewportH
        );
      },
      onPanResponderRelease: () => setLegendDragging(false),
      onPanResponderTerminate: () => setLegendDragging(false),
    });
  }, []);

  useEffect(() => {
    let startX = 0;
    let startScrollX = 0;

    canvasDragResponderRef.current = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gestureState) => Math.abs(gestureState.dx) > 4,
      onPanResponderGrant: (evt) => {
        startX = evt.nativeEvent.pageX;
        startScrollX = scrollPositionRef.current.x;
      },
      onPanResponderMove: (evt) => {
        const deltaX = evt.nativeEvent.pageX - startX;
        const sensitivity = getCanvasPanSensitivity();
        scrollCanvasTo(
          startScrollX - deltaX * sensitivity,
          scrollPositionRef.current.y
        );
      },
    });
  }, []);

  useEffect(() => {
    let startScroll = { x: 0, y: 0 };

    canvasMapPanRef.current = PanResponder.create({
      onStartShouldSetPanResponder: () => draggingTableRef.current === null,
      onMoveShouldSetPanResponder: (_, gestureState) =>
        draggingTableRef.current === null &&
        (Math.abs(gestureState.dx) > 4 || Math.abs(gestureState.dy) > 4),
      onPanResponderGrant: () => {
        startScroll = { ...scrollPositionRef.current };
        canvasScrollRef.current?.setNativeProps({ scrollEnabled: false });
      },
      onPanResponderMove: (_, gestureState) => {
        const horizontalSensitivity = getCanvasPanSensitivity();
        scrollCanvasTo(
          startScroll.x - gestureState.dx * horizontalSensitivity,
          startScroll.y - gestureState.dy
        );
      },
      onPanResponderRelease: () => {
        canvasScrollRef.current?.setNativeProps({ scrollEnabled: draggingTable === null });
      },
      onPanResponderTerminate: () => {
        canvasScrollRef.current?.setNativeProps({ scrollEnabled: draggingTable === null });
      },
    });
  }, [draggingTable]);

  const fetchSchemaData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all tables
      const apiUrl = getApiUrl();
      console.log('Fetching schema data from:', apiUrl);
      const tablesResponse = await fetch(`${apiUrl}/api/schema/tables`);
      
      if (!tablesResponse.ok) {
        throw new Error(`Failed to fetch tables: ${tablesResponse.status} ${tablesResponse.statusText}`);
      }
      
      const tablesData = await tablesResponse.json();
      console.log('Fetched tables:', tablesData.length);
      setTables(tablesData.map(t => t.table_name));
      
      // Fetch junction tables
      try {
        const junctionResponse = await fetch(`${getApiUrl()}/api/schema/junction-tables`);
        if (junctionResponse.ok) {
          const junctionData = await junctionResponse.json();
          if (Array.isArray(junctionData)) {
            setJunctionTables(new Set(junctionData));
          } else {
            console.warn('Junction tables response is not an array:', junctionData);
            setJunctionTables(new Set());
          }
        } else {
          console.warn('Failed to fetch junction tables:', junctionResponse.status);
          setJunctionTables(new Set());
        }
      } catch (err) {
        console.warn('Error fetching junction tables:', err);
        setJunctionTables(new Set());
      }
      
      // Fetch relationships
      const relationshipsResponse = await fetch(`${getApiUrl()}/api/schema/relationships`);
      const relationshipsData = await relationshipsResponse.json();
      setRelationships(relationshipsData);
      
      // Fetch fields for all tables
      const fieldsPromises = tablesData.map(table => 
        fetch(`${getApiUrl()}/api/schema/tables/${table.table_name}/fields`)
          .then(res => res.json())
          .then(fields => ({ table: table.table_name, fields }))
      );
      
      const fieldsResults = await Promise.all(fieldsPromises);
      const fieldsMap = {};
      fieldsResults.forEach(({ table, fields }) => {
        fieldsMap[table] = fields;
      });
      setTableFields(fieldsMap);
      
      // Initialize positions for objects (grid layout)
      const positions = {};
      tablesData.forEach((table, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        // Start with padding and ensure objects are well distributed
        positions[table.table_name] = {
          x: 150 + col * 320,
          y: 150 + row * 300,
        };
      });
      setObjectPositions(positions);
      
      // Auto-select first 6 tables by default so something shows up
      const tableNames = tablesData.map(t => t.table_name);
      const defaultSelected = new Set(tableNames.slice(0, Math.min(6, tableNames.length)));
      console.log('Auto-selected tables:', Array.from(defaultSelected));
      setSelectedTables(defaultSelected);
      
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching schema data:', error);
      setError(error.message || 'Failed to load schema data');
      setLoading(false);
    }
  };

  const handleTableToggle = (tableName) => {
    const newSelected = new Set(selectedTables);
    if (newSelected.has(tableName)) {
      newSelected.delete(tableName);
    } else {
      newSelected.add(tableName);
    }
    setSelectedTables(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTables.size === tables.length) {
      setSelectedTables(new Set());
    } else {
      setSelectedTables(new Set(tables));
    }
  };

  const isJunctionTable = (tableName) => {
    return junctionTables.has(tableName);
  };

  const getRelationshipsForSelectedTables = () => {
    return relationships.filter(rel => 
      selectedTables.has(rel.source_table) && selectedTables.has(rel.target_table)
    );
  };

  const getRelationshipType = (relationship) => {
    // Check if target table has multiple FKs pointing to same source
    const sameTargetCount = relationships.filter(r => 
      r.target_table === relationship.target_table && 
      r.source_table === relationship.source_table
    ).length;
    
    if (sameTargetCount > 1) {
      return 'many:many';
    }
    
    // Check if source column is unique
    const sourceTableFields = tableFields[relationship.source_table] || [];
    const sourceColumn = sourceTableFields.find(f => f.column_name === relationship.source_column);
    
    // For now, assume 1:many unless we can determine otherwise
    return '1:many';
  };

  const handleRelationshipClick = (relationship) => {
    setSelectedRelationship(relationship);
    setShowRelationshipModal(true);
  };
  
  const getPanResponder = (tableName) => {
    // Return cached pan responder if it exists
    if (panRespondersRef.current[tableName]) {
      return panRespondersRef.current[tableName];
    }
    
    // Initialize last position if not exists
    if (!lastPositionsRef.current[tableName]) {
      lastPositionsRef.current[tableName] = objectPositionsRef.current[tableName] || { x: 100, y: 100 };
    }
    
    // Create and cache pan responder - use refs to avoid stale closures
    panRespondersRef.current[tableName] = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true, // Always capture move events
      onPanResponderTerminationRequest: () => false, // Don't allow other responders to take over
      onPanResponderGrant: (evt) => {
        const pos = objectPositionsRef.current[tableName] || { x: 100, y: 100 };
        lastPositionsRef.current[tableName] = { ...pos };
        draggingTableRef.current = tableName;
        setDraggingTable(tableName);
        // Disable scrolling while dragging
        if (canvasScrollRef.current) {
          canvasScrollRef.current.setNativeProps({ scrollEnabled: false });
        }
      },
      onPanResponderMove: (evt, gestureState) => {
        // Use ref to check draggingTable to avoid stale closure
        if (draggingTableRef.current === tableName) {
          const startPos = lastPositionsRef.current[tableName];
          // Direct position update - no animation during drag for immediate response
          const newX = startPos.x + gestureState.dx;
          const newY = startPos.y + gestureState.dy;
          
          // Update lastPositionsRef immediately for next move
          lastPositionsRef.current[tableName] = { x: newX, y: newY };
          
          // Update position immediately
          setObjectPositions(prev => ({
            ...prev,
            [tableName]: {
              x: newX,
              y: newY,
            },
          }));
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        if (draggingTableRef.current === tableName) {
          const startPos = lastPositionsRef.current[tableName];
          // Save final position
          const finalX = startPos.x + gestureState.dx;
          const finalY = startPos.y + gestureState.dy;
          lastPositionsRef.current[tableName] = { x: finalX, y: finalY };
          
          // Re-enable scrolling
          if (canvasScrollRef.current) {
            canvasScrollRef.current.setNativeProps({ scrollEnabled: true });
          }
          
          draggingTableRef.current = null;
          setDraggingTable(null);
          setDragOffset({ x: 0, y: 0 });
        }
      },
      onPanResponderTerminate: () => {
        // Re-enable scrolling if drag is terminated
        if (canvasScrollRef.current) {
          canvasScrollRef.current.setNativeProps({ scrollEnabled: true });
        }
        draggingTableRef.current = null;
        setDraggingTable(null);
      },
    });
    
    return panRespondersRef.current[tableName];
  };

  const getLineCoordinates = (rel) => {
    const sourcePos = objectPositions[rel.source_table];
    const targetPos = objectPositions[rel.target_table];
    if (!sourcePos || !targetPos) return null;
    
    // Box dimensions
    const BOX_WIDTH = 280;
    const BOX_HEADER_HEIGHT = 50; // Approximate header height
    
    // Calculate connection points at box edges (centered horizontally, top edge)
    const sourceCenterX = sourcePos.x + BOX_WIDTH / 2;
    const sourceTopY = sourcePos.y + BOX_HEADER_HEIGHT;
    const targetCenterX = targetPos.x + BOX_WIDTH / 2;
    const targetTopY = targetPos.y + BOX_HEADER_HEIGHT;
    
    // Calculate angle for proper line direction
    const dx = targetCenterX - sourceCenterX;
    const dy = targetTopY - sourceTopY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    return {
      x1: sourceCenterX,
      y1: sourceTopY,
      x2: targetCenterX,
      y2: targetTopY,
      midX: (sourceCenterX + targetCenterX) / 2,
      midY: (sourceTopY + targetTopY) / 2,
      distance: distance,
      angle: Math.atan2(dy, dx) * 180 / Math.PI,
    };
  };

  const styles = getStyles(theme);
  const selectedTablesArray = Array.from(selectedTables);

  const horizontalProgress = useMemo(() => {
    const viewportWidth = canvasLayout.width || width;
    const contentWidth = (CANVAS_MIN_WIDTH + CANVAS_PADDING * 2) * canvasScale;
    const maxScrollX = Math.max(1, contentWidth - viewportWidth);
    return Math.min(1, Math.max(0, scrollPosition.x / maxScrollX));
  }, [canvasLayout.width, canvasScale, scrollPosition.x]);

  const legendData = useMemo(() => {
    if (selectedTablesArray.length === 0) {
      return null;
    }

    const objects = selectedTablesArray.map((name) => {
      const pos = objectPositions[name] || { x: 100, y: 100 };
      return {
        x: pos.x,
        y: pos.y,
        w: OBJECT_BOX_WIDTH,
        h: OBJECT_BOX_HEIGHT,
      };
    });

    const minX = Math.min(...objects.map((o) => o.x));
    const minY = Math.min(...objects.map((o) => o.y));
    const maxX = Math.max(...objects.map((o) => o.x + o.w));
    const maxY = Math.max(...objects.map((o) => o.y + o.h));
    const worldW = Math.max(maxX - minX + 120, 400);
    const worldH = Math.max(maxY - minY + 120, 300);

    const mapW = LEGEND_WIDTH - 12;
    const mapH = LEGEND_HEIGHT - 28;
    const scale = Math.min(mapW / worldW, mapH / worldH);

    const viewportLeft = (scrollPosition.x + CANVAS_PADDING) / canvasScale;
    const viewportTop = (scrollPosition.y + CANVAS_PADDING) / canvasScale;
    const viewportW = (canvasLayout.width || width) / canvasScale;
    const viewportH = (canvasLayout.height || height * 0.7) / canvasScale;

    return {
      objects,
      minX,
      minY,
      maxX,
      maxY,
      scale,
      mapW,
      mapH,
      viewportLeft,
      viewportTop,
      viewportW,
      viewportH,
    };
  }, [
    selectedTablesArray,
    objectPositions,
    scrollPosition,
    canvasScale,
    canvasLayout.width,
    canvasLayout.height,
  ]);

  useEffect(() => {
    legendDataRef.current = legendData;
  }, [legendData]);

  const legendBorderColor = theme.isDarkMode ? '#d4af37' : '#FFFFFF';
  const legendViewportColor = '#FFFFFF';

  if (loading) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <Text style={styles.loadingText}>Loading schema...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Error: {error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={fetchSchemaData}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const visibleRelationships = getRelationshipsForSelectedTables();

  return (
    <View
      style={styles.container}
      data-screen="schema-builder"
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'schema-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View
        style={styles.header}
        data-header="true"
        {...(Platform.OS === 'web' ? { dataSet: { header: 'true' } } : {})}
      >
        <View
          style={styles.hamburgerContainer}
          data-header-element="hamburger"
          {...(Platform.OS === 'web' ? { dataSet: { headerElement: 'hamburger' } } : {})}
        >
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View
          style={styles.breadcrumbContainer}
          data-header-element="breadcrumb"
          {...(Platform.OS === 'web' ? { dataSet: { headerElement: 'breadcrumb', schemaBreadcrumb: 'true' } } : {})}
        >
          <Text style={styles.breadcrumb}>Schema Model Overview</Text>
        </View>
        <View
          style={styles.profileIconContainer}
          data-header-element="profile"
          {...(Platform.OS === 'web' ? { dataSet: { headerElement: 'profile' } } : {})}
        >
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <View style={styles.content}>
        {/* Left Menu */}
        <View 
          style={[
            styles.leftMenu, 
            leftPanelMinimized && styles.leftMenuMinimized
          ]}
        >
          {/* Minimize/Maximize Button */}
          <TouchableOpacity
            style={styles.minimizeButton}
            onPress={() => setLeftPanelMinimized(!leftPanelMinimized)}
            activeOpacity={0.7}
          >
            <Text style={styles.minimizeButtonText}>
              {leftPanelMinimized ? '▶' : '◀'}
            </Text>
          </TouchableOpacity>
          
          {!leftPanelMinimized && (
            <ScrollView style={styles.menuScroll}>
            <TouchableOpacity
              style={styles.selectAllContainer}
              onPress={handleSelectAll}
            >
              <View style={[
                styles.checkbox,
                selectedTables.size === tables.length && styles.checkboxSelected
              ]}>
                {selectedTables.size === tables.length && (
                  <Text style={styles.checkmark}>✓</Text>
                )}
              </View>
              <Text style={styles.selectAllText}>All Objects</Text>
            </TouchableOpacity>

            {tables.map((table) => (
              <TouchableOpacity
                key={table}
                style={styles.menuItem}
                onPress={() => handleTableToggle(table)}
              >
                <View style={[
                  styles.checkbox,
                  selectedTables.has(table) && styles.checkboxSelected
                ]}>
                  {selectedTables.has(table) && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </View>
                <Text style={styles.menuItemText}>{table}</Text>
                {isJunctionTable(table) && (
                  <Text style={styles.junctionIndicator}>⚡</Text>
                )}
              </TouchableOpacity>
            ))}
            </ScrollView>
          )}
        </View>

        {/* Canvas Area */}
        <View
          style={styles.canvasContainer}
          onLayout={(event) => {
            const { width: layoutWidth, height: layoutHeight } = event.nativeEvent.layout;
            setCanvasLayout({ width: layoutWidth, height: layoutHeight });
            canvasLayoutRef.current = { width: layoutWidth, height: layoutHeight };
          }}
        >
          {/* Horizontal page scroller — inset clears the left-pane toggle button */}
          <View
            style={[
              styles.horizontalScroller,
              { left: leftPanelMinimized ? 58 : 44 },
            ]}
            {...(Platform.OS === 'web' ? { dataSet: { schemaHorizontalScroller: 'true' } } : {})}
          >
            <View
              style={styles.scrollerTrackWrap}
              {...(canvasDragResponderRef.current?.panHandlers || {})}
            >
              <View
                style={styles.scrollerTrack}
                onLayout={(event) => {
                  scrollerTrackWidthRef.current = event.nativeEvent.layout.width;
                }}
              >
                <View
                  style={[
                    styles.scrollerFill,
                    { width: `${horizontalProgress * 100}%` },
                  ]}
                />
                <View
                  style={[
                    styles.scrollerThumb,
                    { left: `${horizontalProgress * 100}%` },
                  ]}
                />
              </View>
              <Text
                style={styles.scrollerHint}
                {...(Platform.OS === 'web' ? { dataSet: { schemaScrollerHint: 'true' } } : {})}
              >
                Drag to pan horizontally
              </Text>
            </View>
          </View>

          {/* Zoom Controls */}
          <View style={styles.zoomControls}>
            <TouchableOpacity
              style={styles.zoomButton}
              onPress={handleZoomOut}
              activeOpacity={0.7}
            >
              <Text style={styles.zoomButtonText}>−</Text>
            </TouchableOpacity>
            <View style={styles.zoomGauge}>
              <View style={styles.zoomGaugeTrack}>
                <View 
                  style={[
                    styles.zoomGaugeFill,
                    { width: `${((zoomLevel - 0.5) / 1.5) * 100}%` }
                  ]} 
                />
              </View>
              <Text style={styles.zoomGaugeText}>{Math.round(zoomLevel * 100)}%</Text>
            </View>
            <TouchableOpacity
              style={styles.zoomButton}
              onPress={handleZoomIn}
              activeOpacity={0.7}
            >
              <Text style={styles.zoomButtonText}>+</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.zoomResetButton}
              onPress={handleResetZoom}
              activeOpacity={0.7}
            >
              <Text style={styles.zoomResetButtonText}>Reset</Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            ref={canvasScrollRef}
            nativeID="schema-canvas-scroll"
            style={styles.canvasScroll}
            contentContainerStyle={styles.canvasContent}
            showsVerticalScrollIndicator={true}
            showsHorizontalScrollIndicator={true}
            scrollEnabled={draggingTable === null}
            bounces={false}
            scrollEventThrottle={16}
            onScroll={handleCanvasScroll}
            {...(Platform.OS === 'web'
              ? { dataSet: { schemaCanvasScroll: 'true' } }
              : pinchResponderRef.current?.panHandlers)}
          >
            <View style={{ transform: [{ scale: canvasScale }] }}>
            <View
              style={[
                styles.canvasPanLayer,
                { minWidth: CANVAS_MIN_WIDTH, minHeight: CANVAS_MIN_HEIGHT },
              ]}
              {...(canvasMapPanRef.current?.panHandlers || {})}
            />
            {/* Relationship Lines Layer (behind objects) */}
            {visibleRelationships.map((rel, index) => {
              const coords = getLineCoordinates(rel);
              if (!coords) return null;
              
              const relType = getRelationshipType(rel);
              const isManyToMany = relType === 'many:many';
              const angle = coords.angle;
              const distance = coords.distance;
              
              return (
                <View key={index} style={styles.relationshipLineWrapper}>
                  <View
                    style={[
                      styles.relationshipLineAbsolute,
                      {
                        left: coords.x1,
                        top: coords.y1,
                        width: distance,
                        height: 2,
                        transform: [{ rotate: `${angle}deg` }],
                      }
                    ]}
                  >
                    <View
                      style={[
                        styles.relationshipLine,
                        isManyToMany && styles.relationshipLineDashed,
                        { width: distance },
                      ]}
                    />
                  </View>
                  
                  {/* Label positioned at midpoint */}
                  <TouchableOpacity
                    style={[
                      styles.relationshipLabel,
                      {
                        left: coords.midX - 30,
                        top: coords.midY - 12,
                      }
                    ]}
                    onPress={() => handleRelationshipClick(rel)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.relationshipLabelText}>{relType}</Text>
                  </TouchableOpacity>
                </View>
              );
            })}

            {/* Empty State */}
            {selectedTablesArray.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>
                  No objects selected. Select objects from the left menu to view them on the canvas.
                </Text>
              </View>
            )}

            {/* Object Boxes */}
            {selectedTablesArray.map((tableName) => {
              const pos = objectPositions[tableName] || { x: 100, y: 100 };
              const fields = tableFields[tableName] || [];
              const isJunction = isJunctionTable(tableName);
              const panResponder = getPanResponder(tableName);
              const isDragging = draggingTable === tableName;
              
              return (
                <View
                  key={tableName}
                  {...panResponder.panHandlers}
                  style={[
                    styles.objectBox,
                    isJunction && styles.objectBoxJunction,
                    isDragging && styles.objectBoxDragging,
                    { 
                      left: pos.x, 
                      top: pos.y,
                    },
                  ]}
                >
                  <View style={styles.objectHeader}>
                    <Text style={styles.objectName}>{tableName}</Text>
                    {isJunction && (
                      <Text style={styles.junctionLabel}>⚡ Junction</Text>
                    )}
                  </View>
                  <ScrollView
                    style={styles.fieldsList}
                    scrollEnabled={!isDragging}
                    nestedScrollEnabled={true}
                    showsVerticalScrollIndicator={true}
                    {...(Platform.OS === 'web' ? { dataSet: { schemaObjectFieldsScroll: 'true' } } : {})}
                  >
                    {fields.map((field, idx) => (
                      <View key={idx} style={styles.fieldItem}>
                        <Text style={styles.fieldName}>{field.column_name}</Text>
                        <Text style={styles.fieldType}>
                          {field.data_type}
                          {field.character_maximum_length && `(${field.character_maximum_length})`}
                          {field.is_nullable === 'NO' && ' NOT NULL'}
                        </Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              );
            })}
            </View>
          </ScrollView>

          {legendData && (
            <View style={styles.legendContainer} pointerEvents="box-none">
              <View style={styles.legendTopRow} pointerEvents="none">
                <View style={[styles.legendTopLine, { borderColor: legendBorderColor }]} />
                <Text style={[styles.legendTitle, { color: legendBorderColor }]}>Legend</Text>
                <View style={[styles.legendTopLine, { borderColor: legendBorderColor }]} />
              </View>
              <View
                style={[
                  styles.legendMap,
                  {
                    borderLeftColor: legendBorderColor,
                    borderRightColor: legendBorderColor,
                    borderBottomColor: legendBorderColor,
                  },
                ]}
                pointerEvents="box-none"
              >
                {legendData.objects.map((obj, index) => (
                  <View
                    key={`legend-obj-${index}`}
                    pointerEvents="none"
                    style={[
                      styles.legendObjectOutline,
                      {
                        left: (obj.x - legendData.minX) * legendData.scale + 4,
                        top: (obj.y - legendData.minY) * legendData.scale + 4,
                        width: Math.max(4, obj.w * legendData.scale),
                        height: Math.max(3, obj.h * legendData.scale),
                      },
                    ]}
                  />
                ))}
                <View
                  {...(legendPanRef.current?.panHandlers || {})}
                  style={[
                    styles.legendViewportTouch,
                    legendDragging && styles.legendViewportDragging,
                    {
                      left: (legendData.viewportLeft - legendData.minX) * legendData.scale,
                      top: (legendData.viewportTop - legendData.minY) * legendData.scale,
                      width: Math.max(16, legendData.viewportW * legendData.scale + 8),
                      height: Math.max(14, legendData.viewportH * legendData.scale + 8),
                    },
                  ]}
                >
                  <View
                    pointerEvents="none"
                    style={[
                      styles.legendViewport,
                      {
                        borderColor: legendViewportColor,
                        backgroundColor: theme.isDarkMode
                          ? 'rgba(255, 255, 255, 0.18)'
                          : 'rgba(255, 255, 255, 0.12)',
                        width: Math.max(8, legendData.viewportW * legendData.scale),
                        height: Math.max(6, legendData.viewportH * legendData.scale),
                      },
                    ]}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Relationship Details Modal */}
      <Modal
        visible={showRelationshipModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowRelationshipModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowRelationshipModal(false)}
            >
              <Text style={styles.modalCloseButtonText}>✕</Text>
            </TouchableOpacity>
            
            {selectedRelationship && (
              <>
                <Text style={styles.modalTitle}>Relationship Details</Text>
                <View style={styles.relationshipDetails}>
                  <Text style={styles.detailLabel}>Source Table:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.source_table}</Text>
                  
                  <Text style={styles.detailLabel}>Source Column:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.source_column}</Text>
                  
                  <Text style={styles.detailLabel}>Target Table:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.target_table}</Text>
                  
                  <Text style={styles.detailLabel}>Target Column:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.target_column}</Text>
                  
                  <Text style={styles.detailLabel}>Relationship Type:</Text>
                  <Text style={styles.detailValue}>
                    {getRelationshipType(selectedRelationship)}
                  </Text>
                  
                  <Text style={styles.detailLabel}>On Delete:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.delete_rule}</Text>
                  
                  <Text style={styles.detailLabel}>On Update:</Text>
                  <Text style={styles.detailValue}>{selectedRelationship.update_rule}</Text>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    overflow: 'visible',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 10,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  hamburgerContainer: {
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
    marginLeft: 10,
    backgroundColor: 'transparent',
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
    backgroundColor: 'transparent',
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'visible',
  },
  leftMenu: {
    width: 250,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRightWidth: 1,
    borderRightColor: theme.colors.border,
    position: 'relative',
    overflow: 'visible',
    zIndex: 100,
  },
  leftMenuMinimized: {
    width: 50,
  },
  minimizeButton: {
    position: 'absolute',
    top: 10,
    right: -20,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.colors.primary || '#d4af37',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    borderWidth: 2,
    borderColor: theme.isDarkMode ? '#000' : '#FFF',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.3,
      shadowRadius: 5,
      elevation: 10,
    }),
  },
  minimizeButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  horizontalScroller: {
    position: 'absolute',
    top: 6,
    right: 12,
    zIndex: 50,
  },
  scrollerTrackWrap: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 8,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' ? {
      cursor: 'grab',
    } : {}),
  },
  scrollerTrack: {
    width: '100%',
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : 'rgba(255, 255, 255, 0.9)',
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    overflow: 'visible',
    position: 'relative',
  },
  scrollerFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.colors.primary || '#d4af37',
    opacity: 0.45,
    borderRadius: 5,
  },
  scrollerThumb: {
    position: 'absolute',
    top: -8,
    width: 36,
    height: 40,
    marginLeft: -18,
    borderRadius: 8,
    backgroundColor: theme.colors.primary || '#d4af37',
    borderWidth: 3,
    borderColor: theme.isDarkMode ? '#000' : '#FFF',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.4,
      shadowRadius: 3,
      elevation: 4,
    }),
  },
  scrollerHint: {
    marginTop: 4,
    fontSize: 10,
    fontWeight: '600',
    color: theme.isDarkMode ? '#d4af37' : '#8a6f1f',
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  canvasPanLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 0,
    backgroundColor: 'transparent',
  },
  legendContainer: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    width: LEGEND_WIDTH,
    zIndex: 120,
  },
  legendTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: -1,
  },
  legendTopLine: {
    flex: 1,
    borderTopWidth: 2,
    marginTop: 8,
  },
  legendTitle: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.4,
    paddingHorizontal: 8,
    backgroundColor: '#000',
  },
  legendMap: {
    width: '100%',
    height: LEGEND_HEIGHT - 16,
    backgroundColor: '#000',
    borderLeftWidth: 2,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    position: 'relative',
    overflow: 'hidden',
  },
  legendObjectOutline: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: '#d4af37',
    backgroundColor: 'transparent',
  },
  legendViewportTouch: {
    position: 'absolute',
    zIndex: 10,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' ? {
      cursor: 'grab',
    } : {}),
  },
  legendViewport: {
    borderWidth: 2,
  },
  legendViewportDragging: {
    opacity: 0.75,
    ...(Platform.OS === 'web' ? {
      cursor: 'grabbing',
    } : {}),
  },
  menuScroll: {
    flex: 1,
  },
  selectAllContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  checkboxSelected: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  selectAllText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    paddingLeft: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? '#333' : '#DDD',
  },
  menuItemText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text,
  },
  junctionIndicator: {
    fontSize: 16,
    marginLeft: 5,
  },
  canvasContainer: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
    position: 'relative',
  },
  zoomControls: {
    position: 'absolute',
    top: 68,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
    borderRadius: 8,
    padding: 8,
    zIndex: 100,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  zoomButton: {
    width: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: theme.colors.primary || '#d4af37',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 4,
  },
  zoomButtonText: {
    color: '#000',
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  zoomGauge: {
    marginHorizontal: 8,
    alignItems: 'center',
    minWidth: 100,
  },
  zoomGaugeTrack: {
    width: '100%',
    height: 6,
    backgroundColor: theme.isDarkMode ? '#444' : '#DDD',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  zoomGaugeFill: {
    height: '100%',
    backgroundColor: theme.colors.primary || '#d4af37',
    borderRadius: 3,
  },
  zoomGaugeText: {
    fontSize: 11,
    color: theme.colors.text,
    fontWeight: '600',
  },
  zoomResetButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: theme.isDarkMode ? '#333' : '#EEE',
    marginLeft: 4,
  },
  zoomResetButtonText: {
    fontSize: 11,
    color: theme.colors.text,
    fontWeight: '600',
  },
  canvasScroll: {
    flex: 1,
    marginTop: 62,
    ...(Platform.OS === 'web' ? {
      overflow: 'scroll',
      height: '100%',
    } : {}),
  },
  canvasContent: {
    minWidth: CANVAS_MIN_WIDTH,
    minHeight: CANVAS_MIN_HEIGHT,
    position: 'relative',
    padding: CANVAS_PADDING,
  },
  relationshipLineWrapper: {
    position: 'absolute',
    zIndex: 1,
    pointerEvents: 'box-none',
  },
  relationshipLineAbsolute: {
    position: 'absolute',
    zIndex: 1,
    ...(Platform.OS === 'web' ? {
      transformOrigin: 'left center',
    } : {}),
  },
  relationshipLine: {
    height: 2,
    backgroundColor: '#d4af37',
    opacity: 0.8,
  },
  relationshipLineDashed: {
    borderStyle: 'dashed',
    borderWidth: 2,
    borderTopWidth: 2,
    borderBottomWidth: 2,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderColor: '#d4af37',
    backgroundColor: 'transparent',
    height: 0,
  },
  relationshipLabel: {
    position: 'absolute',
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#d4af37',
    zIndex: 2,
    minWidth: 50,
    alignItems: 'center',
  },
  relationshipLabelText: {
    fontSize: 10,
    color: '#d4af37',
    fontWeight: '600',
  },
  objectBox: {
    position: 'absolute',
    width: 280,
    minHeight: 150,
    maxHeight: 400,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    padding: 10,
    zIndex: 10,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      cursor: 'grab',
      userSelect: 'none',
      touchAction: 'none',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  objectBoxJunction: {
    borderStyle: 'dashed',
    borderColor: theme.isDarkMode ? '#888' : '#999',
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F9F9F9',
  },
  objectBoxDragging: {
    opacity: 0.9,
    zIndex: 100,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 16px rgba(212, 175, 55, 0.5)',
      cursor: 'grabbing',
    } : {
      elevation: 10,
    }),
  },
  objectHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  objectName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.heading,
    flex: 1,
  },
  junctionLabel: {
    fontSize: 10,
    color: theme.isDarkMode ? '#888' : '#666',
    fontStyle: 'italic',
    marginLeft: 5,
  },
  fieldsList: {
    maxHeight: 300,
  },
  fieldItem: {
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
  },
  fieldName: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.text,
  },
  fieldType: {
    fontSize: 11,
    color: theme.isDarkMode ? '#999' : '#666',
    marginTop: 2,
  },
  loadingText: {
    fontSize: 18,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: 50,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.9,
    maxWidth: 500,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    position: 'relative',
  },
  modalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  modalCloseButtonText: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  relationshipDetails: {
    gap: 10,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    marginTop: 8,
  },
  detailValue: {
    fontSize: 14,
    color: theme.isDarkMode ? '#CCC' : '#333',
    marginLeft: 10,
  },
});
